import { z } from 'zod';

export const fileSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  folderId: z.string().uuid().nullable(),
  name: z.string().min(1).max(255),
  originalName: z.string().min(1).max(255),
  mimeType: z.string(),
  size: z.number().int().min(0),
  r2Key: z.string(),
  r2Bucket: z.string(),
  checksum: z.string().nullable().optional(),
  thumbnailKey: z.string().nullable().optional(),
  isStarred: z.boolean().default(false),
  isTrashed: z.boolean().default(false),
  trashedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type File = z.infer<typeof fileSchema>;

export const filePublicSchema = fileSchema.omit({
  userId: true,
  r2Key: true,
  r2Bucket: true,
  checksum: true,
});

export type FilePublic = z.infer<typeof filePublicSchema>;

// Límite de 5GB por archivo
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

// MIME types permitidos (whitelist)
const ALLOWED_MIME_TYPES = [
  // Imágenes
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/tiff',
  // Documentos
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Texto
  'text/plain',
  'text/csv',
  'text/html',
  'text/css',
  'text/javascript',
  'text/markdown',
  'text/x-markdown',
  'application/json',
  'application/xml',
  // Archivos comprimidos
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-tar',
  // Audio
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/mp4',
  // Video
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
  'video/x-msvideo',
  // Otros
  'application/octet-stream',
] as const;

export const uploadUrlRequestSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.string().refine(
    (val) => ALLOWED_MIME_TYPES.includes(val as typeof ALLOWED_MIME_TYPES[number]),
    { message: 'File type not allowed' }
  ),
  size: z.number().int().positive().max(MAX_FILE_SIZE, 'File size exceeds 5GB limit'),
  folderId: z.string().uuid().nullable().optional(),
});

export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;

export const uploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  fileId: z.string().uuid(),
  r2Key: z.string(),
});

export type UploadUrlResponse = z.infer<typeof uploadUrlResponseSchema>;

export const updateFileRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  folderId: z.string().uuid().nullable().optional(),
  isStarred: z.boolean().optional(),
});

export type UpdateFileRequest = z.infer<typeof updateFileRequestSchema>;

export const bulkActionRequestSchema = z.object({
  fileIds: z.array(z.string().uuid()).min(1),
  action: z.enum(['delete', 'restore', 'move', 'star', 'unstar']),
  folderId: z.string().uuid().optional(),
});

export type BulkActionRequest = z.infer<typeof bulkActionRequestSchema>;
